import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import {
  Plus, Trash2, Save, Sparkles, Loader2, Activity, Stethoscope, Eye, Heart,
  Dumbbell, Move, Repeat, Crosshair, FileSignature, ExternalLink, AlertTriangle
} from 'lucide-react';
import SignaturePad from '../../components/SignaturePad';
import PatientIdentityHeader from '../../components/PatientIdentityHeader';
import Combobox from '../../components/Combobox';
import PrintPicker from '../../components/PrintPicker';
import PrintSignatureBlock from '../../components/PrintSignatureBlock';
import FDarNotesCard from '../../components/FDarNotesCard';
import SectionAiScribe from '../../components/SectionAiScribe';
import useAutosave, { AutosaveIndicator } from '../../hooks/useAutosave';
import SmartSelect from '../../components/SmartSelect';
import AiIcdSuggester from '../../components/AiIcdSuggester';
import VitalSignsCard from '../../components/VitalSignsCard';
import MmtCard from '../../components/MmtCard';
import SpecialTestsCard from '../../components/SpecialTestsCard';
import RomCard from '../../components/RomCard';
import NeurologyCard from '../../components/NeurologyCard';
import FlexibilityCard from '../../components/FlexibilityCard';
import { ALL_SPECIAL_TESTS, ROM_JOINTS, MMT_MUSCLES, MMT_GRADES as MMT_GRADE_OPTS, AUSC_FINDINGS, POSTURAL, ENDFEEL, POS_NEG } from '../../lib/pe-options';

const OBJECTIVE_GROUPS = [
  { title: 'Baseline · Vital signs', items: [
    { id: 'sec-vitals',     label: 'Whole vitals card',  kind: 'section' },
    { id: 'fld-v-bp',       label: 'Blood pressure',     kind: 'field' },
    { id: 'fld-v-hr',       label: 'Heart rate',         kind: 'field' },
    { id: 'fld-v-rr',       label: 'Respiratory rate',   kind: 'field' },
    { id: 'fld-v-spo2',     label: 'SpO₂',               kind: 'field' },
    { id: 'fld-v-temp',     label: 'Temperature',        kind: 'field' },
  ]},
  { title: 'Baseline · Other', items: [
    { id: 'sec-ausc',       label: 'Auscultation table', kind: 'section' },
    { id: 'sec-obs',        label: 'Whole observation card', kind: 'section' },
    { id: 'fld-obs-posture',  label: 'Posture',  kind: 'field' },
    { id: 'fld-obs-gait',     label: 'Gait',     kind: 'field' },
    { id: 'fld-obs-swelling', label: 'Swelling', kind: 'field' },
    { id: 'fld-obs-skin',     label: 'Skin / scars', kind: 'field' },
    { id: 'fld-obs-notes',    label: 'Observation notes', kind: 'field' },
  ]},
  { title: 'Movement', items: [
    { id: 'sec-mmt',     label: 'Manual muscle testing (MMT)', kind: 'section' },
    { id: 'sec-rom',     label: 'Range of motion (ROM)',       kind: 'section' },
    { id: 'sec-flex',    label: 'Flexibility testing',         kind: 'section' },
  ]},
  { title: 'Neuro / Balance', items: [
    { id: 'sec-neuro',   label: 'Neurology (reflexes, myotomes, dermatomes)', kind: 'section' },
    { id: 'sec-bal',     label: 'Balance',         kind: 'section' },
  ]},
  { title: 'Cardiovascular', items: [
    { id: 'sec-cardio',     label: 'Whole cardiovascular card', kind: 'section' },
    { id: 'fld-cv-rpe',     label: 'Borg RPE',             kind: 'field' },
    { id: 'fld-cv-6mwt',    label: '6-min walk',           kind: 'field' },
    { id: 'fld-cv-pre',     label: 'Pre HR/BP',            kind: 'field' },
    { id: 'fld-cv-peak',    label: 'Peak HR/BP',           kind: 'field' },
    { id: 'fld-cv-recovery',label: 'Recovery HR',          kind: 'field' },
    { id: 'fld-cv-proc',    label: 'Procedure',            kind: 'field' },
  ]},
  { title: 'Performance', items: [
    { id: 'sec-1rm',     label: '1RM test',         kind: 'section' },
    { id: 'sec-func',    label: 'Functional tests', kind: 'section' },
    { id: 'sec-lsi',     label: 'Limb Symmetry Index', kind: 'section' },
  ]},
  { title: 'Diagnosis & Plan', items: [
    { id: 'sec-special',    label: 'Special evidence-based tests', kind: 'section' },
    { id: 'sec-dx',         label: 'Whole diagnosis / plan card', kind: 'section' },
    { id: 'fld-dx-plan',    label: 'Plan of action',          kind: 'field' },
    { id: 'fld-dx-tx',      label: 'Treatment',               kind: 'field' },
    { id: 'fld-dx-rx',      label: 'Exercise prescription',   kind: 'field' },
    { id: 'fld-dx-text',    label: 'Working diagnosis',       kind: 'field' },
    { id: 'fld-dx-summary', label: 'Diagnosis text / summary', kind: 'field' },
    { id: 'fld-dx-icd10',   label: 'ICD-10 codes',            kind: 'field' },
    { id: 'fld-dx-icd9',    label: 'ICD-9 codes',             kind: 'field' },
    { id: 'fld-dx-icf',     label: 'ICF codes',               kind: 'field' },
    { id: 'fld-dx-signature', label: 'Patient signature',     kind: 'field' },
  ]},
];

const MMT_GRADES = ['0','1','2-','2','2+','3-','3','3+','4-','4','4+','5'];
const SIDES = ['L','R','Both','N/A'];

// Iter 74 — append an AI-suggested ICD code+name into the diagnosis_text, but
// only if that exact code isn't already in there. Keeps the text and the
// structured icd10_codes array in lockstep without duplicating rows.
function appendIcdToText(current, suggestion) {
  const stamp = `[${suggestion.code} — ${suggestion.name}]`;
  if ((current || '').includes(suggestion.code)) return current || '';
  return ((current || '').trim() + (current ? '\n' : '') + stamp);
}
const REGIONS = [
  { code: 'all', label: 'All regions' },
  { code: 'upper', label: 'Upper limb' },
  { code: 'lower', label: 'Lower limb' },
  { code: 'trunk', label: 'Trunk / spine' },
];

const GAIT_OPTIONS = [
  '', 'Normal', 'Antalgic', 'Ataxic', 'Hemiplegic', 'Spastic diplegic',
  'Trendelenburg', 'Steppage / foot-drop', 'Parkinsonian / festinating',
  'Scissor', 'Waddling (myopathic)', 'Apraxic', 'Cautious / fear-of-falling',
  'Vestibular', 'Hemiparetic', 'Quadriceps-avoidance', 'Stiff-knee', 'Toe-walking',
];

// Joint+motion combined dropdown with reference normal AROM values.
// Source: AAOS / Norkin & White 5th ed.
const ROM_TESTS = [
  { region: 'upper', label: 'Shoulder · Flexion', joint: 'Shoulder', motion: 'Flexion', normal: '180°' },
  { region: 'upper', label: 'Shoulder · Extension', joint: 'Shoulder', motion: 'Extension', normal: '60°' },
  { region: 'upper', label: 'Shoulder · Abduction', joint: 'Shoulder', motion: 'Abduction', normal: '180°' },
  { region: 'upper', label: 'Shoulder · Adduction', joint: 'Shoulder', motion: 'Adduction', normal: '50°' },
  { region: 'upper', label: 'Shoulder · Internal rotation', joint: 'Shoulder', motion: 'Internal rotation', normal: '70°' },
  { region: 'upper', label: 'Shoulder · External rotation', joint: 'Shoulder', motion: 'External rotation', normal: '90°' },
  { region: 'upper', label: 'Elbow · Flexion', joint: 'Elbow', motion: 'Flexion', normal: '150°' },
  { region: 'upper', label: 'Elbow · Extension', joint: 'Elbow', motion: 'Extension', normal: '0°' },
  { region: 'upper', label: 'Forearm · Pronation', joint: 'Forearm', motion: 'Pronation', normal: '80°' },
  { region: 'upper', label: 'Forearm · Supination', joint: 'Forearm', motion: 'Supination', normal: '80°' },
  { region: 'upper', label: 'Wrist · Flexion', joint: 'Wrist', motion: 'Flexion', normal: '80°' },
  { region: 'upper', label: 'Wrist · Extension', joint: 'Wrist', motion: 'Extension', normal: '70°' },
  { region: 'upper', label: 'Wrist · Radial deviation', joint: 'Wrist', motion: 'Radial deviation', normal: '20°' },
  { region: 'upper', label: 'Wrist · Ulnar deviation', joint: 'Wrist', motion: 'Ulnar deviation', normal: '30°' },
  { region: 'trunk', label: 'Cervical · Flexion', joint: 'Cervical', motion: 'Flexion', normal: '45°' },
  { region: 'trunk', label: 'Cervical · Extension', joint: 'Cervical', motion: 'Extension', normal: '45°' },
  { region: 'trunk', label: 'Cervical · Lateral flexion', joint: 'Cervical', motion: 'Lateral flexion', normal: '45°' },
  { region: 'trunk', label: 'Cervical · Rotation', joint: 'Cervical', motion: 'Rotation', normal: '60°' },
  { region: 'trunk', label: 'Thoracolumbar · Flexion', joint: 'Thoracolumbar', motion: 'Flexion', normal: '80°' },
  { region: 'trunk', label: 'Thoracolumbar · Extension', joint: 'Thoracolumbar', motion: 'Extension', normal: '30°' },
  { region: 'trunk', label: 'Thoracolumbar · Lateral flexion', joint: 'Thoracolumbar', motion: 'Lateral flexion', normal: '35°' },
  { region: 'trunk', label: 'Thoracolumbar · Rotation', joint: 'Thoracolumbar', motion: 'Rotation', normal: '45°' },
  { region: 'lower', label: 'Hip · Flexion', joint: 'Hip', motion: 'Flexion', normal: '120°' },
  { region: 'lower', label: 'Hip · Extension', joint: 'Hip', motion: 'Extension', normal: '30°' },
  { region: 'lower', label: 'Hip · Abduction', joint: 'Hip', motion: 'Abduction', normal: '45°' },
  { region: 'lower', label: 'Hip · Adduction', joint: 'Hip', motion: 'Adduction', normal: '30°' },
  { region: 'lower', label: 'Hip · Internal rotation', joint: 'Hip', motion: 'Internal rotation', normal: '45°' },
  { region: 'lower', label: 'Hip · External rotation', joint: 'Hip', motion: 'External rotation', normal: '45°' },
  { region: 'lower', label: 'Knee · Flexion', joint: 'Knee', motion: 'Flexion', normal: '135°' },
  { region: 'lower', label: 'Knee · Extension', joint: 'Knee', motion: 'Extension', normal: '0°' },
  { region: 'lower', label: 'Ankle · Dorsiflexion', joint: 'Ankle', motion: 'Dorsiflexion', normal: '20°' },
  { region: 'lower', label: 'Ankle · Plantarflexion', joint: 'Ankle', motion: 'Plantarflexion', normal: '50°' },
  { region: 'lower', label: 'Subtalar · Inversion', joint: 'Subtalar', motion: 'Inversion', normal: '35°' },
  { region: 'lower', label: 'Subtalar · Eversion', joint: 'Subtalar', motion: 'Eversion', normal: '15°' },
];

const FLEXIBILITY_TESTS = [
  'Sit-and-reach', 'Modified sit-and-reach', 'Thomas test', 'Modified Thomas',
  'Ober test', 'Ely test', 'Straight-leg raise (SLR)', '90/90 hamstring',
  'Popliteal angle', 'Apley scratch', 'Shoulder flexion (supine)',
  'Cervical lateral flexion', 'Forward bend (finger-to-floor)', 'Schober test',
  'Side-bend (trunk lateral flexion)', 'FABER test', 'Trunk rotation',
];

// Grouped neurology tests + reference link per test (Physiopedia where available).
const NEURO_TESTS = [
  { group: 'Reflexes', label: 'Biceps reflex (C5–C6)',     link: 'https://www.physio-pedia.com/Deep_Tendon_Reflexes' },
  { group: 'Reflexes', label: 'Triceps reflex (C7)',       link: 'https://www.physio-pedia.com/Deep_Tendon_Reflexes' },
  { group: 'Reflexes', label: 'Patellar reflex (L3–L4)',   link: 'https://www.physio-pedia.com/Deep_Tendon_Reflexes' },
  { group: 'Reflexes', label: 'Achilles reflex (S1)',      link: 'https://www.physio-pedia.com/Deep_Tendon_Reflexes' },
  { group: 'Reflexes', label: 'Babinski sign',             link: 'https://www.physio-pedia.com/Babinski_Sign' },
  { group: 'Myotomes', label: 'C5 — Shoulder abduction',   link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Myotomes', label: 'C6 — Elbow flexion / wrist ext.', link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Myotomes', label: 'C7 — Elbow extension',      link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Myotomes', label: 'C8 — Thumb extension',      link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Myotomes', label: 'T1 — Finger abduction',     link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Myotomes', label: 'L2 — Hip flexion',          link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Myotomes', label: 'L3 — Knee extension',       link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Myotomes', label: 'L4 — Ankle dorsiflexion',   link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Myotomes', label: 'L5 — Great toe extension',  link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Myotomes', label: 'S1 — Ankle plantarflexion', link: 'https://www.physio-pedia.com/Myotomes' },
  { group: 'Dermatomes', label: 'C5 — Lateral upper arm',  link: 'https://www.physio-pedia.com/Dermatomes' },
  { group: 'Dermatomes', label: 'C6 — Thumb',              link: 'https://www.physio-pedia.com/Dermatomes' },
  { group: 'Dermatomes', label: 'C7 — Middle finger',      link: 'https://www.physio-pedia.com/Dermatomes' },
  { group: 'Dermatomes', label: 'C8 — Little finger',      link: 'https://www.physio-pedia.com/Dermatomes' },
  { group: 'Dermatomes', label: 'T1 — Medial forearm',     link: 'https://www.physio-pedia.com/Dermatomes' },
  { group: 'Dermatomes', label: 'L4 — Medial leg',         link: 'https://www.physio-pedia.com/Dermatomes' },
  { group: 'Dermatomes', label: 'L5 — Dorsum of foot',     link: 'https://www.physio-pedia.com/Dermatomes' },
  { group: 'Dermatomes', label: 'S1 — Lateral foot',       link: 'https://www.physio-pedia.com/Dermatomes' },
  { group: 'Neurodynamic', label: 'Upper-limb tension test (ULNT 1 – median)', link: 'https://www.physio-pedia.com/Upper_Limb_Neurodynamic_Test_1' },
  { group: 'Neurodynamic', label: 'ULNT 2a – median (shoulder depression)',    link: 'https://www.physio-pedia.com/Upper_Limb_Neurodynamic_Test_2a' },
  { group: 'Neurodynamic', label: 'ULNT 2b – radial nerve',                    link: 'https://www.physio-pedia.com/Upper_Limb_Neurodynamic_Test_2b' },
  { group: 'Neurodynamic', label: 'ULNT 3 – ulnar nerve',                      link: 'https://www.physio-pedia.com/Upper_Limb_Neurodynamic_Test_3' },
  { group: 'Neurodynamic', label: 'Slump test',                                link: 'https://www.physio-pedia.com/Slump_Test' },
  { group: 'Neurodynamic', label: 'Straight-leg raise (neural)',               link: 'https://www.physio-pedia.com/Straight_Leg_Raise_Test' },
  { group: 'Neurodynamic', label: 'Prone-knee bend (femoral nerve)',           link: 'https://www.physio-pedia.com/Prone_Knee_Bend_Test' },
];

const SECTION_DEFAULTS = {
  obj_section_vitals: true, obj_section_auscultation: true, obj_section_observation: true,
  obj_section_mmt: true, obj_section_rom: true, obj_section_flexibility: true,
  obj_section_neurology: true, obj_section_balance: true, obj_section_cardio: true,
  obj_section_one_rm: true, obj_section_functional: true, obj_section_lsi: true,
  obj_section_special: true, obj_section_diagnosis: true,
};

// Vital-sign normal ranges. Out-of-range values render in red.
const VITAL_RANGES = {
  hr:   { lo: 60,  hi: 100, label: 'HR (bpm)' },
  rr:   { lo: 12,  hi: 20,  label: 'RR (br/min)' },
  spo2: { lo: 95,  hi: 100, label: 'SpO₂ (%)' },
  temp: { lo: 36.1, hi: 37.5, label: 'Temp (°C)' },
};

function isVitalAbnormal(k, raw) {
  if (k === 'bp') {
    // Expect "120/80" format
    const m = (raw || '').match(/(\d+)\s*\/\s*(\d+)/);
    if (!m) return false;
    const sys = parseInt(m[1]); const dia = parseInt(m[2]);
    return sys < 90 || sys > 130 || dia < 60 || dia > 85;
  }
  const r = VITAL_RANGES[k]; if (!r) return false;
  const v = parseFloat(raw); if (!Number.isFinite(v)) return false;
  return v < r.lo || v > r.hi;
}

export default function ObjectiveAssessment() {
  const { patient_id } = useParams();
  const [patient, setPatient] = useState(null);
  const [settings, setSettings] = useState(SECTION_DEFAULTS);
  const [obj, setObj] = useState({
    patient_id,
    muscle_tests: [], goniometer: [], neurology: [], vitals: {}, balance: [], cardiovascular: {},
    auscultation: [], observation: {}, flexibility: [], one_rm: [], functional: [], lsi: [],
    special_tests: [], history: [],
    diagnosis_text: '', plan_of_action: '', treatment_text: '', exercise_text: '',
    therapist_notes: '', diagnosis: '', icd10_codes: [], icd11_codes: [], icd9_codes: [], icf_codes: [],
    patient_signature: '', signed_at: '',
    // Iter 68 — F-DAR (Focus / Data / Action / Response) ad-hoc note
    fdar_focus: '', fdar_data: '', fdar_action: '', fdar_response: '',
  });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const enabled = (k) => settings[k] !== false;

  // Iter 69 — universal autosave + resume-after-sleep. Drafts persist for 7d
  // in localStorage so a phone going to sleep mid-exam doesn't lose data.
  const { lastSavedAt, restore, clear } = useAutosave(`objective:${patient_id}`, obj);

  useEffect(() => {
    const draft = restore();
    if (draft && typeof draft === 'object') {
      setObj(prev => ({ ...prev, ...draft }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get(`/patients/${patient_id}`).then(r => setPatient(r.data));
    api.get(`/objective/${patient_id}`).then(r => setObj(o => ({ ...o, ...r.data })));
    api.get(`/interviews/${patient_id}`).then(r => setObj(o => ({
      ...o,
      therapist_notes: o.therapist_notes || r.data?.therapist_notes || '',
      diagnosis: o.diagnosis || r.data?.diagnosis || '',
      icd10_codes: (o.icd10_codes?.length ? o.icd10_codes : r.data?.icd10_codes) || [],
      icd9_codes:  (o.icd9_codes?.length  ? o.icd9_codes  : r.data?.icd9_codes)  || [],
      icf_codes:   (o.icf_codes?.length   ? o.icf_codes   : r.data?.icf_codes)   || [],
    }))).catch(() => {});
    api.get('/clinic/settings').then(r => setSettings({ ...SECTION_DEFAULTS, ...r.data })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient_id]);

  const save = async () => {
    setSaving(true);
    try {
      // Backend owns the history snapshot — we just send the current state.
      const { data: persisted } = await api.post('/objective', obj);
      if (persisted) setObj(persisted);
      // Persist diagnosis + ICD/ICF onto the interview so they round-trip with intake metadata.
      try {
        await api.post('/interviews', {
          patient_id, diagnosis: obj.diagnosis, therapist_notes: obj.therapist_notes,
          icd10_codes: obj.icd10_codes, icd9_codes: obj.icd9_codes, icf_codes: obj.icf_codes,
        });
      } catch (e) { /* non-fatal */ }
      // Clear local autosave draft once the server save succeeds.
      clear();
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  const addRow = (key, row) => setObj(o => ({ ...o, [key]: [...(o[key] || []), row] }));
  const updRow = (key, i, patch) => setObj(o => {
    const arr = [...(o[key] || [])]; arr[i] = { ...arr[i], ...patch }; return { ...o, [key]: arr };
  });
  const delRow = (key, i) => setObj(o => ({ ...o, [key]: (o[key] || []).filter((_, idx) => idx !== i) }));

  // Iter 70 — append an AI scribe transcript as a new row at the bottom of
  // any table-based section. The clinician can then edit it inline.
  const appendScribeRow = (sectionKey, rowFactory) => (text) => {
    const row = rowFactory(text);
    setObj(o => ({ ...o, [sectionKey]: [...(o[sectionKey] || []), row] }));
  };

  const setVital = (k, v) => setObj(o => ({ ...o, vitals: { ...(o.vitals || {}), [k]: v } }));
  const setCardio = (k, v) => setObj(o => ({ ...o, cardiovascular: { ...(o.cardiovascular || {}), [k]: v } }));
  const setObs = (k, v) => setObj(o => ({ ...o, observation: { ...(o.observation || {}), [k]: v } }));

  const [regionFilter, setRegionFilter] = useState('all');

  // AI suggest — objective tests
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState('');
  const aiSuggest = async () => {
    setAiBusy(true); setAiNote('');
    try {
      const { data } = await api.post('/ai/objective-suggest', { patient_id, region: regionFilter });
      setObj(o => ({
        ...o,
        muscle_tests: [...(o.muscle_tests || []), ...(data.muscle_tests || []).map(t => ({
          muscle: t.muscle, side: t.side || 'Both', grade: '', notes: '', region: t.region,
        }))],
        goniometer: [...(o.goniometer || []), ...(data.goniometer || []).map(t => ({
          joint: t.joint, motion: t.motion, side: t.side || 'Both', aROM: '', pROM: '',
        }))],
        neurology: [...(o.neurology || []), ...(data.neurology || []).map(t => ({
          test: t.test, side: t.side || 'Both', result: '', notes: '',
        }))],
      }));
      setAiNote(data.rationale || 'Tests appended below. Edit / remove as needed.');
    } catch (e) {
      setAiNote('AI suggest failed: ' + (e.response?.data?.detail || e.message));
    } finally { setAiBusy(false); }
  };

  // AI suggest — diagnosis + plan + treatment + exercise → and pre-fill the treatment plan.
  const [aiTxBusy, setAiTxBusy] = useState(false);
  const aiTreatmentSuggest = async () => {
    setAiTxBusy(true);
    try {
      const { data } = await api.post('/ai/treatment-suggest', { patient_id });
      setObj(o => ({
        ...o,
        diagnosis_text: o.diagnosis_text || data.diagnosis || '',
        plan_of_action: o.plan_of_action || data.plan_of_action || '',
        treatment_text: o.treatment_text || data.treatment || '',
        exercise_text: o.exercise_text || data.exercise || '',
      }));
      // Pre-fill the patient-signed Treatment Plan with AI starter content (idempotent —
      // only writes when the existing plan is empty so we never overwrite signed work).
      try {
        const existing = (await api.get(`/treatment-plans/${patient_id}`)).data || {};
        if (!existing.short_term_goals && !existing.interventions) {
          await api.post('/treatment-plans', {
            patient_id,
            short_term_goals: data.plan_of_action || '',
            interventions: data.treatment || '',
            long_term_goals: data.exercise || '',
            icd10_codes: obj.icd10_codes || [], icf_codes: obj.icf_codes || [],
          });
          setAiNote((data.rationale || '') + ' · Treatment plan starter saved — go sign at /admin/patients/' + patient_id + '/treatment-plan');
          return;
        }
      } catch (_) { /* non-fatal */ }
      setAiNote('Treatment suggestion: ' + (data.rationale || ''));
    } catch (e) {
      setAiNote('AI treatment suggest failed: ' + (e.response?.data?.detail || e.message));
    } finally { setAiTxBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl print:p-4">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4 print:mb-2">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-2">PART 2 · OBJECTIVE ASSESSMENT</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Physical examination</h1>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          <select className="input max-w-[150px] text-sm" value={regionFilter}
                  onChange={e=>setRegionFilter(e.target.value)} data-testid="region-filter">
            {REGIONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
          <button onClick={aiSuggest} disabled={aiBusy} className="btn-secondary inline-flex items-center gap-2" data-testid="ai-suggest-tests">
            {aiBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4 text-brand"/>}
            {aiBusy ? 'Thinking…' : 'AI suggest tests'}
          </button>
          <PrintPicker
            storageKey="objective"
            patientId={patient_id}
            form="objective"
            groups={OBJECTIVE_GROUPS}
            onPrint={() => window.print()}
          />
          <AutosaveIndicator lastSavedAt={lastSavedAt} onClear={clear}/>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="obj-save">
            <Save className="w-4 h-4"/> {saving?'Saving…':'Save'}
          </button>
        </div>
      </div>

      <PatientIdentityHeader patient={patient} suffix="Part 2 · Objective"/>

      {saved && <div className="mb-4 badge-success w-fit">Saved.</div>}
      {aiNote && (
        <div className="mb-4 bento-card text-sm text-fg-secondary border-l-4 border-l-brand" data-testid="ai-note">
          <span className="text-brand font-medium">AI:</span> {aiNote}
        </div>
      )}

      {(obj.history || []).length > 0 && (
        <div className="mb-4 surface border border-soft rounded-lg p-3 text-xs text-fg-muted" data-testid="progression-strip">
          <strong className="text-brand">Progression:</strong> {obj.history.length} saved assessment{obj.history.length === 1 ? '' : 's'}.
          Latest: {new Date(obj.history[obj.history.length-1].at).toLocaleString()}. Older entries preserved.
        </div>
      )}

      {enabled('obj_section_vitals') && (
        <VitalSignsCard
          vitals={obj.vitals || {}}
          setVital={setVital}
          isVitalAbnormal={isVitalAbnormal}
          Section={Section}
        />
      )}

      {enabled('obj_section_auscultation') && (
      <Section title="Auscultation" icon={Stethoscope} testid="sec-ausc"
               scribeKey="auscultation" patientId={patient_id}
               onScribeText={appendScribeRow('auscultation', (text) => ({ site: 'AI', finding: text, reference: '', note: '🎙 dictated' }))}>
        <p className="text-xs text-fg-muted mb-3">Reference normal: vesicular breath sounds, S1+S2 regular. Add a row for each repeat reading.</p>
        <GridTable cols={['Site','Finding','Reference','Date / Note']}>
          {(obj.auscultation || []).map((r, i) => (
            <tr key={i} data-testid={`ausc-${i}`}>
              <td><input className="input" value={r.site||''} onChange={e=>updRow('auscultation', i, { site: e.target.value })} placeholder="R upper lobe, apex…"/></td>
              <td>
                <SmartSelect value={r.finding || ''}
                             onChange={(v) => updRow('auscultation', i, { finding: v })}
                             options={AUSC_FINDINGS}
                             placeholder="Pick finding or Other…"
                             testid={`ausc-${i}-finding`}
                             compact/>
              </td>
              <td><input className="input" value={r.reference||''} onChange={e=>updRow('auscultation', i, { reference: e.target.value })} placeholder="Normal: clear"/></td>
              <td><input className="input" value={r.note||''} onChange={e=>updRow('auscultation', i, { note: e.target.value })} placeholder="dd-mm or comments"/></td>
              <td className="print:hidden"><button onClick={()=>delRow('auscultation', i)} className="text-[#B83A3A] p-1"><Trash2 className="w-4 h-4"/></button></td>
            </tr>
          ))}
        </GridTable>
        <AddBtn onClick={()=>addRow('auscultation', { site:'', finding:'', reference:'', note:'' })} testid="add-ausc"/>
      </Section>
      )}

      {enabled('obj_section_observation') && (
      <Section title="Physical observation" icon={Eye} testid="sec-obs">
        <div className="grid md:grid-cols-2 gap-3">
          <div data-print-field="fld-obs-posture"><TextField label="Posture" value={obj.observation?.posture||''} onChange={v=>setObs('posture', v)} testid="obs-posture"/></div>
          <label className="block" data-print-field="fld-obs-gait">
            <div className="text-xs text-fg-muted mb-1">Gait</div>
            <select className="input" value={obj.observation?.gait || ''}
                    onChange={e=>setObs('gait', e.target.value)} data-testid="obs-gait">
              {GAIT_OPTIONS.map(g => <option key={g} value={g}>{g || '—'}</option>)}
            </select>
          </label>
          <div data-print-field="fld-obs-swelling"><TextField label="Swelling / edema" value={obj.observation?.swelling||''} onChange={v=>setObs('swelling', v)} testid="obs-swelling"/></div>
          <div data-print-field="fld-obs-skin"><TextField label="Skin / scars" value={obj.observation?.skin||''} onChange={v=>setObs('skin', v)} testid="obs-skin"/></div>
          <label className="md:col-span-2 block" data-print-field="fld-obs-notes">
            <div className="text-xs text-fg-muted mb-1">Notes</div>
            <textarea className="input min-h-[60px]" value={obj.observation?.notes||''} onChange={e=>setObs('notes', e.target.value)} data-testid="obs-notes"/>
          </label>
        </div>
      </Section>
      )}

      {enabled('obj_section_mmt') && (
      /* Iter 77 — MMT + SpecialTests now extracted into dedicated components
         (`MmtCard`, `SpecialTestsCard`). Row handlers + scribe wiring passed
         in as props so the parent keeps owning state. ROM/Neuro/Flexibility
         remain inline pending a follow-up `useObjectiveRows()` hook. */
      <MmtCard
        rows={obj.muscle_tests || []}
        regionFilter={regionFilter}
        patient_id={patient_id}
        updRow={updRow} addRow={addRow} delRow={delRow}
        appendScribeRow={appendScribeRow}
        Section={Section} GridTable={GridTable} AddBtn={AddBtn}
      />
      )}

      {enabled('obj_section_rom') && (
      <RomCard
        rows={obj.goniometer || []}
        regionFilter={regionFilter}
        romTests={ROM_TESTS}
        patient_id={patient_id}
        updRow={updRow} addRow={addRow} delRow={delRow}
        appendScribeRow={appendScribeRow}
        Section={Section} GridTable={GridTable} AddBtn={AddBtn}
      />
      )}

      {enabled('obj_section_flexibility') && (
      <FlexibilityCard
        rows={obj.flexibility || []}
        flexibilityTests={FLEXIBILITY_TESTS}
        updRow={updRow} addRow={addRow} delRow={delRow}
        Section={Section} GridTable={GridTable} AddBtn={AddBtn}
      />
      )}

      {enabled('obj_section_neurology') && (
      <NeurologyCard
        rows={obj.neurology || []}
        neuroTests={NEURO_TESTS}
        updRow={updRow} addRow={addRow} delRow={delRow}
        Section={Section} GridTable={GridTable} AddBtn={AddBtn}
      />
      )}

      {enabled('obj_section_balance') && (
      <Section title="Balance" testid="sec-bal">
        <GridTable cols={['Test','Result','Notes']}>
          {(obj.balance || []).map((r, i) => (
            <tr key={i} data-testid={`bal-${i}`}>
              <td><input className="input" value={r.test||''} onChange={e=>updRow('balance', i, { test: e.target.value })} placeholder="Berg, Romberg, SLB…"/></td>
              <td><input className="input" value={r.result||''} onChange={e=>updRow('balance', i, { result: e.target.value })}/></td>
              <td><input className="input" value={r.notes||''} onChange={e=>updRow('balance', i, { notes: e.target.value })}/></td>
              <td className="print:hidden"><button onClick={()=>delRow('balance', i)} className="text-[#B83A3A] p-1"><Trash2 className="w-4 h-4"/></button></td>
            </tr>
          ))}
        </GridTable>
        <AddBtn onClick={()=>addRow('balance', { test:'', result:'', notes:'' })} testid="add-bal"/>
      </Section>
      )}

      {enabled('obj_section_cardio') && (
      <Section title="Cardiovascular testing" icon={Heart} testid="sec-cardio">
        <p className="text-xs text-fg-muted mb-3">Criteria: HR, BP, RPE before / during / after. Procedure: Bruce / 6MWT / step test as appropriate.</p>
        <div className="grid md:grid-cols-3 gap-3">
          <label data-print-field="fld-cv-rpe"><div className="text-xs text-fg-muted mb-1">Borg RPE (6–20)</div><input className="input" value={obj.cardiovascular?.rpe||''} onChange={e=>setCardio('rpe', e.target.value)} data-testid="cv-rpe"/></label>
          <label data-print-field="fld-cv-6mwt"><div className="text-xs text-fg-muted mb-1">6-min walk test (m)</div><input className="input" value={obj.cardiovascular?.six_min_walk_m||''} onChange={e=>setCardio('six_min_walk_m', e.target.value)} data-testid="cv-6mwt"/></label>
          <label data-print-field="fld-cv-pre"><div className="text-xs text-fg-muted mb-1">Pre HR / BP</div><input className="input" value={obj.cardiovascular?.pre||''} onChange={e=>setCardio('pre', e.target.value)} data-testid="cv-pre"/></label>
          <label data-print-field="fld-cv-peak"><div className="text-xs text-fg-muted mb-1">Peak HR / BP</div><input className="input" value={obj.cardiovascular?.peak||''} onChange={e=>setCardio('peak', e.target.value)} data-testid="cv-peak"/></label>
          <label data-print-field="fld-cv-recovery"><div className="text-xs text-fg-muted mb-1">Recovery HR (1 min)</div><input className="input" value={obj.cardiovascular?.recovery||''} onChange={e=>setCardio('recovery', e.target.value)} data-testid="cv-recovery"/></label>
          <label data-print-field="fld-cv-proc"><div className="text-xs text-fg-muted mb-1">Procedure</div><input className="input" value={obj.cardiovascular?.procedure||''} onChange={e=>setCardio('procedure', e.target.value)} placeholder="Bruce / 6MWT / step…" data-testid="cv-proc"/></label>
          <label className="md:col-span-3"><div className="text-xs text-fg-muted mb-1">Notes</div><textarea className="input" value={obj.cardiovascular?.notes||''} onChange={e=>setCardio('notes', e.target.value)}/></label>
        </div>
      </Section>
      )}

      {enabled('obj_section_one_rm') && (
      <Section title="1RM test" icon={Dumbbell} testid="sec-1rm">
        <p className="text-xs text-fg-muted mb-3">Estimated 1-rep max — Brzycki / Epley formula or direct lift. Add a row per lift.</p>
        <GridTable cols={['Exercise','Method','Weight (kg)','Reps','Estimated 1RM (kg)','Notes']}>
          {(obj.one_rm || []).map((r, i) => (
            <tr key={i} data-testid={`onerm-${i}`}>
              <td><input className="input" value={r.exercise||''} onChange={e=>updRow('one_rm', i, { exercise: e.target.value })} placeholder="Bench, squat, deadlift…"/></td>
              <td><select className="input" value={r.method||''} onChange={e=>updRow('one_rm', i, { method: e.target.value })}>
                <option value="">—</option>
                <option value="brzycki">Brzycki</option><option value="epley">Epley</option>
                <option value="direct">Direct</option>
              </select></td>
              <td><input className="input" value={r.weight||''} onChange={e=>updRow('one_rm', i, { weight: e.target.value })}/></td>
              <td><input className="input" value={r.reps||''} onChange={e=>updRow('one_rm', i, { reps: e.target.value })}/></td>
              <td><input className="input" value={r.one_rm||''} onChange={e=>updRow('one_rm', i, { one_rm: e.target.value })}/></td>
              <td><input className="input" value={r.notes||''} onChange={e=>updRow('one_rm', i, { notes: e.target.value })}/></td>
              <td className="print:hidden"><button onClick={()=>delRow('one_rm', i)} className="text-[#B83A3A] p-1"><Trash2 className="w-4 h-4"/></button></td>
            </tr>
          ))}
        </GridTable>
        <AddBtn onClick={()=>addRow('one_rm', { exercise:'', method:'', weight:'', reps:'', one_rm:'', notes:'' })} testid="add-1rm"/>
      </Section>
      )}

      {enabled('obj_section_functional') && (
      <Section title="Functional tests" icon={Repeat} testid="sec-func">
        <p className="text-xs text-fg-muted mb-3">FMS, Y-Balance, single-leg hop, TUG, sit-to-stand, etc.</p>
        <GridTable cols={['Test','Result','Reference / norm','Notes']}>
          {(obj.functional || []).map((r, i) => (
            <tr key={i} data-testid={`func-${i}`}>
              <td><input className="input" value={r.test||''} onChange={e=>updRow('functional', i, { test: e.target.value })} placeholder="Y-Balance, TUG…"/></td>
              <td><input className="input" value={r.result||''} onChange={e=>updRow('functional', i, { result: e.target.value })}/></td>
              <td><input className="input" value={r.reference||''} onChange={e=>updRow('functional', i, { reference: e.target.value })} placeholder="age-matched norm"/></td>
              <td><input className="input" value={r.notes||''} onChange={e=>updRow('functional', i, { notes: e.target.value })}/></td>
              <td className="print:hidden"><button onClick={()=>delRow('functional', i)} className="text-[#B83A3A] p-1"><Trash2 className="w-4 h-4"/></button></td>
            </tr>
          ))}
        </GridTable>
        <AddBtn onClick={()=>addRow('functional', { test:'', result:'', reference:'', notes:'' })} testid="add-func"/>
      </Section>
      )}

      {enabled('obj_section_lsi') && (
      <Section title="Limb Symmetry Index (LSI)" testid="sec-lsi">
        <p className="text-xs text-fg-muted mb-3">LSI = (involved / uninvolved) × 100. ≥ 90% generally indicates symmetry / return-to-sport readiness.</p>
        <GridTable cols={['Test','Involved (Inv)','Uninvolved (Unv)','LSI %','Notes']}>
          {(obj.lsi || []).map((r, i) => {
            const lsi = (() => {
              const inv = parseFloat(r.involved); const unv = parseFloat(r.uninvolved);
              return (inv > 0 && unv > 0) ? ((inv / unv) * 100).toFixed(1) : '';
            })();
            return (
              <tr key={i} data-testid={`lsi-${i}`}>
                <td><input className="input" value={r.test||''} onChange={e=>updRow('lsi', i, { test: e.target.value })} placeholder="Single hop, side hop…"/></td>
                <td><input className="input" value={r.involved||''} onChange={e=>updRow('lsi', i, { involved: e.target.value })}/></td>
                <td><input className="input" value={r.uninvolved||''} onChange={e=>updRow('lsi', i, { uninvolved: e.target.value })}/></td>
                <td className="font-mono text-sm">{lsi}{lsi && '%'}</td>
                <td><input className="input" value={r.notes||''} onChange={e=>updRow('lsi', i, { notes: e.target.value })}/></td>
                <td className="print:hidden"><button onClick={()=>delRow('lsi', i)} className="text-[#B83A3A] p-1"><Trash2 className="w-4 h-4"/></button></td>
              </tr>
            );
          })}
        </GridTable>
        <AddBtn onClick={()=>addRow('lsi', { test:'', involved:'', uninvolved:'', notes:'' })} testid="add-lsi"/>
      </Section>
      )}

      {enabled('obj_section_special') && (
      <SpecialTestsCard
        rows={obj.special_tests || []}
        patient_id={patient_id}
        updRow={updRow} addRow={addRow} delRow={delRow}
        appendScribeRow={appendScribeRow}
        Section={Section} GridTable={GridTable} AddBtn={AddBtn}
      />
      )}

      {enabled('obj_section_diagnosis') && (
      <Section title="Diagnosis · Plan · Treatment & Therapist notes" icon={FileSignature} testid="sec-dx">
        <div className="flex justify-end mb-3 print:hidden">
          <button onClick={aiTreatmentSuggest} disabled={aiTxBusy}
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="ai-suggest-treatment">
            {aiTxBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4 text-brand"/>}
            {aiTxBusy ? 'Thinking…' : 'AI suggest + pre-fill treatment plan'}
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div data-print-field="fld-dx-text"><LongField label="Working diagnosis" value={obj.diagnosis} onChange={v=>setObj(o=>({...o, diagnosis: v}))} testid="dx-diagnosis"/></div>
          <LongField label="Therapist notes" value={obj.therapist_notes} onChange={v=>setObj(o=>({...o, therapist_notes: v}))} testid="dx-therapist-notes"/>
          <div data-print-field="fld-dx-plan"><LongField label="Plan of action" value={obj.plan_of_action} onChange={v=>setObj(o=>({...o, plan_of_action: v}))} testid="dx-plan"/></div>
          <div data-print-field="fld-dx-tx"><LongField label="Treatment" value={obj.treatment_text} onChange={v=>setObj(o=>({...o, treatment_text: v}))} testid="dx-treatment"/></div>
          <div data-print-field="fld-dx-rx"><LongField label="Exercise prescription" value={obj.exercise_text} onChange={v=>setObj(o=>({...o, exercise_text: v}))} testid="dx-exercise"/></div>
          <div data-print-field="fld-dx-summary"><LongField label="Diagnosis text (assessment summary)" value={obj.diagnosis_text} onChange={v=>setObj(o=>({...o, diagnosis_text: v}))} testid="dx-text"/></div>
        </div>

        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <div className="md:col-span-3" data-print-field="fld-dx-icd10">
            <Combobox endpoint="/lookup/icd10" value={obj.icd10_codes || []}
                      onChange={v=>setObj(o=>({...o, icd10_codes: v}))}
                      label="ICD-10 — Diagnosis"
                      placeholder="Search ICD-10 diagnosis codes…"
                      testid="dx-icd10"/>
            {/* Iter 74 — AI ICD suggester is bidirectional: clicking a suggestion
                appends the code to BOTH the structured icd10_codes array AND
                the diagnosis_text field, so it shows up on the chart and the
                PDF / referral letter without manual re-entry. */}
            <div className="mt-2">
              <AiIcdSuggester
                text={[obj.diagnosis_text, obj.diagnosis, obj.therapist_notes].filter(Boolean).join('\n\n')}
                patientId={patient_id}
                testidPrefix="dx-ai-icd"
                onPick={(s) => setObj(o => ({
                  ...o,
                  icd10_codes: Array.from(new Set([...(o.icd10_codes || []), s.code])),
                  diagnosis_text: appendIcdToText(o.diagnosis_text, s),
                }))}/>
            </div>
          </div>
          {settings.feature_icd11 && (
            <div className="md:col-span-3" data-print-field="fld-dx-icd11">
              <Combobox endpoint="/lookup/icd11" value={obj.icd11_codes || []}
                        onChange={v=>setObj(o=>({...o, icd11_codes: v}))}
                        label="ICD-11 (WHO 2022) — Diagnosis"
                        placeholder="Search ICD-11 codes…"
                        testid="dx-icd11"/>
            </div>
          )}
          <div className="md:col-span-3" data-print-field="fld-dx-icd9">
            <Combobox endpoint="/lookup/icd9" value={obj.icd9_codes || []}
                      onChange={v=>setObj(o=>({...o, icd9_codes: v}))}
                      label="ICD-9 — Procedures performed"
                      placeholder="Search ICD-9-CM procedures…"
                      testid="dx-icd9"/>
          </div>
          <div className="md:col-span-3" data-print-field="fld-dx-icf">
            <Combobox endpoint="/lookup/icf" value={obj.icf_codes || []}
                      onChange={v=>setObj(o=>({...o, icf_codes: v}))}
                      label="ICF — Functioning"
                      placeholder="Search ICF musculoskeletal codes…"
                      testid="dx-icf"/>
          </div>
        </div>

        <div className="mt-6 surface border border-soft rounded-lg p-4" data-print-field="fld-dx-signature">
          <h4 className="font-heading font-semibold text-sm mb-1 flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-brand"/> Patient agreement & signature
          </h4>
          <p className="text-xs text-fg-muted mb-3">I have read the proposed plan of action and consent to the recommended treatment and exercise prescription.</p>
          <SignaturePad value={obj.patient_signature || ''}
                        onChange={(v)=>setObj(o=>({...o, patient_signature: v, signed_at: v ? new Date().toISOString() : ''}))}/>
          {obj.signed_at && (
            <div className="text-xs text-fg-muted mt-2">Signed at {new Date(obj.signed_at).toLocaleString()}</div>
          )}
        </div>
      </Section>
      )}

      {/* Iter 68 — F-DAR (Focus / Data / Action / Response) ad-hoc note for the
          objective examination. Saved as part of the objective payload. */}
      <div className="mt-4">
        <FDarNotesCard
          value={{
            focus: obj.fdar_focus,
            data: obj.fdar_data,
            action: obj.fdar_action,
            response: obj.fdar_response,
          }}
          onChange={(k, v) => setObj(o => ({ ...o, [`fdar_${k}`]: v }))}
          discipline="physiotherapy"
          testidPrefix="objective-fdar"
        />
      </div>

      <div className="hidden print:block text-xs text-fg-muted mt-6 pt-3 border-t border-soft">
        Physiogenic · Part 2 Objective assessment · Generated {new Date().toLocaleString()}
      </div>
      <PrintSignatureBlock leftLabel="Clinician / Provider" rightLabel="Patient" />
    </div>
  );
}

function Section({ title, icon: Icon, children, testid, printId, scribeKey, patientId, onScribeText }) {
  // Iter 70 — per-section AI Scribe button when scribeKey is provided.
  return (
    <div className="admin-card mb-4" data-testid={testid} data-print-section={printId || testid}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-brand"/>}
          {title}
        </h3>
        {scribeKey && patientId && onScribeText && (
          <SectionAiScribe
            patientId={patientId}
            section={scribeKey}
            onTranscript={onScribeText}
            testid={`scribe-${scribeKey}`}
          />
        )}
      </div>
      {children}
    </div>
  );
}

function GridTable({ cols, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead><tr>{cols.map(c=><th key={c}>{c}</th>)}<th className="print:hidden"></th></tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function AddBtn({ onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="mt-2 text-xs text-brand inline-flex items-center gap-1 print:hidden">
      <Plus className="w-3.5 h-3.5"/> Add row
    </button>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      <input className="input" value={value} onChange={e=>onChange(e.target.value)} data-testid={testid}/>
    </label>
  );
}

function LongField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      <textarea className="input min-h-[100px]" value={value || ''} onChange={e=>onChange(e.target.value)} data-testid={testid}/>
    </label>
  );
}
